import classnames from 'classnames';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { useUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import type { UnderlyingsFieldMetadata } from 'models/forms/fields/UnderlyingsFieldMetadata';
import type { Underlying } from 'models/underlying';
import { UnderlyingSearch } from 'models/underlying/underlying-search';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';
import {
  BatchSingleUnderlying,
  BatchSingleUnderlyingWithCutoff,
} from './SingleUnderlyingComponent/BatchSingleUnderlyingFieldComponent.connect';

type Props = ProductNodeBuilderProps & { useOrientation?: boolean };

export function BatchSingleUnderlyingField(props: Props) {
  const underlyingsSearch = useUnderlyingSearchByFormId(props.tabId, props.node.id);
  return <BatchSingleUnderlyingWrapper {...props} {...underlyingsSearch} />;
}
export function BatchSingleUnderlyingWrapper({
  node: { id },
  className,
  useOrientation = true,
  tabIndex,
  search,
  getOptionsFromPastedLabels,
}: Props & UnderlyingSearch) {
  const intl = useIntl();
  const [currentValue, setValue] = useData<readonly Underlying[]>(id, []);
  const isFieldInvalid = useIsFieldInvalid(id);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const underlyingsFieldMetadata = useFieldMetadata<UnderlyingsFieldMetadata>(id);
  const { isCellInactive, isSelected, lostInputBeforeEditing, onEditingKeyDown, setLostInputBeforeEditing } =
    useContext(BatchNavigationContext);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  const resetLostInputBeforeEditing = () => setLostInputBeforeEditing?.(undefined);
  const handleUnderlyingReplaced = (underlying: Underlying | undefined) => {
    setValue(underlying ? [underlying] : []);
    resetLostInputBeforeEditing();
  };

  const handleUnderlyingUpdated = (underlying: Underlying | undefined) => {
    setValue(underlying ? [underlying] : []);
    resetLostInputBeforeEditing();
  };

  const componentWithCutoff = (
    <BatchSingleUnderlyingWithCutoff
      id={id}
      search={search}
      getOptionsFromPastedLabels={getOptionsFromPastedLabels}
      key={id}
      value={currentValue.at(0)}
      onChange={handleUnderlyingReplaced}
      onCutOffChange={handleUnderlyingUpdated}
      isClearable
      isDisabled={isReadOnly}
      placeholder={intl.formatMessage({ id: 'underlying.placeholder' })}
      className={classnames(className, { 'is-warning': isFieldInvalid })}
      tooltipPlacement="right"
      tabIndex={tabIndex}
      isFocused={isSelected}
      isCellInactive={isCellInactive}
      onEditingKeyDown={onEditingKeyDown}
      lostInputBeforeEditing={lostInputBeforeEditing}
      onTagChanged={resetLostInputBeforeEditing}
      onInputChanged={resetLostInputBeforeEditing}
    />
  );
  const component = (
    <BatchSingleUnderlying
      id={id}
      key={id}
      search={search}
      getOptionsFromPastedLabels={getOptionsFromPastedLabels}
      value={currentValue.at(0)}
      onChange={handleUnderlyingReplaced}
      isClearable
      isDisabled={isReadOnly}
      placeholder={intl.formatMessage({ id: 'underlying.placeholder' })}
      className={classnames(className, { 'is-warning': isFieldInvalid })}
      tooltipPlacement="right"
      tabIndex={tabIndex}
      isFocused={isSelected}
      isCellInactive={isCellInactive}
      onEditingKeyDown={onEditingKeyDown}
      lostInputBeforeEditing={lostInputBeforeEditing}
      onTagChanged={resetLostInputBeforeEditing}
      onInputChanged={resetLostInputBeforeEditing}
    />
  );
  const componentToRender = underlyingsFieldMetadata?.hasCutOff ? componentWithCutoff : component;
  if (useOrientation) {
    return (
      <BlockOrientationFormGroup inputId={id} workspaceType="newBatch" className={patchedFieldClassName}>
        {componentToRender}
      </BlockOrientationFormGroup>
    );
  }
  return componentToRender;
}
BatchSingleUnderlyingField.displayName = 'BatchSingleUnderlyingField';
